"use client";

import { useRef, useState } from "react";
import { type Control, type FieldValues, type Path } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Upload, ImageIcon, Loader2, Cloud } from "lucide-react";
import { useSession } from "next-auth/react";
import { uploadToS3, type S3ImageMetadata } from "@admin/_lib/s3-upload";
import { toast } from "sonner";

interface S3UploadFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  description?: string;
  disabled?: boolean;
  accept?: string;
  bucket: string;
  path: string;
}

export function S3UploadField<T extends FieldValues>({
  control,
  name,
  label,
  description,
  disabled = false,
  accept = "image/*",
  bucket,
  path,
}: S3UploadFieldProps<T>) {
  const { data: session } = useSession();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        // Normalize field.value into an array of S3ImageMetadata
        let currentFiles: any[] = [];
        if (Array.isArray(field.value)) {
          currentFiles = field.value;
        } else if (typeof field.value === "string" && (field.value as string).trim() !== "") {
          // Legacy string URL fallback
          const val = field.value as string;
          currentFiles = [{
            imgUrl: val,
            name: val.split("/").pop() || "legacy-image",
            size: 0,
            type: "image/unknown",
            fileservername: field.value,
            path: "",
            bucket: bucket,
            baseurl: ""
          }];
        } else if (field.value && typeof field.value === "object") {
          currentFiles = [field.value];
        }

        const hasFiles = currentFiles.length > 0;

        async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
          const file = e.target.files?.[0];
          if (!file || !session?.token) return;

          setIsUploading(true);
          try {
            const metadata = await uploadToS3(file, path, bucket, session.token);
            field.onChange([...currentFiles, metadata]);
            toast.success("File uploaded successfully");
          } catch (err: unknown) {
            toast.error(err instanceof Error ? err.message : "S3 upload failed");
          } finally {
            setIsUploading(false);
            if (inputRef.current) inputRef.current.value = "";
          }
        }

        function removeFile(index: number) {
          const newFiles = currentFiles.filter((_, i) => i !== index);
          field.onChange(newFiles);
        }

        return (
          <FormItem className="space-y-4">
            <FormLabel className="text-base font-semibold flex items-center gap-2">
              <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
              {label}
            </FormLabel>
            <FormControl>
              <div className="space-y-4">
                <input
                  ref={inputRef}
                  type="file"
                  accept={accept}
                  disabled={disabled || isUploading}
                  className="sr-only"
                  onChange={handleFileChange}
                />

                {!disabled && (
                  <div 
                    onClick={() => !isUploading && inputRef.current?.click()}
                    className="relative group cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed transition-all hover:bg-white/5 active:scale-[0.99]"
                    style={{ borderColor: "hsl(240 20% 20%)" }}
                  >
                    <div className="flex flex-col items-center justify-center py-10 px-6 gap-3 text-center">
                      <div className="relative">
                         <div className="absolute inset-0 blur-xl bg-orange-500/20 rounded-full group-hover:bg-orange-500/30 transition-all" />
                         <div className="relative size-12 rounded-full bg-zinc-900 border border-white/10 flex items-center justify-center text-orange-500 shadow-xl group-hover:scale-110 transition-transform">
                            {isUploading ? <Loader2 className="size-6 animate-spin" /> : <Upload className="size-6" />}
                         </div>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-foreground">
                          {isUploading ? "Uploading file..." : "Click or drag to upload"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Support for JPG, PNG, GIF, WEBP (Max 5MB)
                        </p>
                      </div>
                      
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        disabled={isUploading}
                        className="mt-2 text-[10px] uppercase tracking-wider font-bold text-orange-500 hover:text-orange-400 hover:bg-orange-500/10"
                      >
                        Browse Files
                      </Button>
                    </div>
                  </div>
                )}

                {(hasFiles || isUploading) && (
                   <div className="space-y-3">
                     <div className="flex items-center justify-between px-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                           <span className="size-1 rounded-full bg-orange-500" />
                           Uploaded Files ({currentFiles.length})
                        </p>
                     </div>
                     
                     <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                       {currentFiles.map((file, i) => (
                         <div
                           key={i}
                           className="group relative flex flex-col items-center gap-3 rounded-2xl border p-2 shadow-2xl transition-all hover:border-orange-500/50 hover:bg-white/5"
                           style={{
                             background: "hsl(240 20% 10%)",
                             borderColor: "hsl(240 20% 18%)",
                           }}
                         >
                           <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-black/40 shadow-inner flex items-center justify-center">
                             <img
                               src={file.imgUrl}
                               alt={file.name}
                               className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                             />
                             <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                             
                             <div className="absolute top-2 right-2 flex gap-1 transform translate-y-[-10px] opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                               <div className="bg-orange-500/90 backdrop-blur-sm rounded-lg p-1.5 shadow-lg border border-white/20">
                                 <Cloud className="size-3 text-white" />
                               </div>
                             </div>
                           </div>

                           <div className="w-full flex items-center justify-between gap-2 px-1 pb-1">
                             <div className="min-w-0">
                               <p className="truncate text-[10px] font-bold text-foreground/90 uppercase tracking-tight">
                                 {file.name}
                               </p>
                               <p className="text-[9px] text-muted-foreground mt-0.5">
                                 {(file.size / 1024).toFixed(1)} KB • {file.type.split("/")[1]}
                               </p>
                             </div>
                             
                             {!disabled && (
                               <button
                                 type="button"
                                 onClick={() => removeFile(i)}
                                 className="size-7 rounded-lg bg-zinc-800 flex items-center justify-center text-muted-foreground hover:bg-destructive hover:text-white transition-colors"
                                 title="Remove file"
                               >
                                 <ImageIcon className="size-3" />
                               </button>
                             )}
                           </div>
                         </div>
                       ))}

                       {isUploading && (
                         <div
                           className="flex aspect-video flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed animate-pulse bg-white/5"
                           style={{ borderColor: "hsl(240 20% 20%)" }}
                         >
                           <Loader2 className="size-6 animate-spin text-orange-500" />
                           <p className="text-[9px] font-bold uppercase tracking-widest text-orange-500">Processing...</p>
                         </div>
                       )}
                     </div>
                   </div>
                )}

                {!hasFiles && !isUploading && disabled && (
                  <div
                    className="flex h-32 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed text-muted-foreground"
                    style={{ borderColor: "hsl(240 20% 20%)" }}
                  >
                    <ImageIcon className="size-8 opacity-20" />
                    <p className="text-xs italic">No images available</p>
                  </div>
                )}
              </div>
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
